import { useState, useEffect } from 'react';
import { HeaderShareBell } from './HeaderShareBell';

const TZ_PRESETS = [
  { tz: 'America/New_York', city: 'NEW YORK', label: 'EASTERN' },
  { tz: 'America/Los_Angeles', city: 'LOS ANGELES', label: 'PACIFIC' },
  { tz: 'Europe/London', city: 'LONDON', label: 'GMT' },
  { tz: 'Asia/Tokyo', city: 'TOKYO', label: 'JST' },
];

function Clock({
  tz,
  city,
  label,
}: {
  tz: string;
  city: string;
  label: string;
}) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const formatted = now.toLocaleString('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  const [time, ampm] = formatted.split(' ');
  const [h, m, s] = time.split(':');

  return (
    <div className="flex flex-col items-end gap-px">
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-mono font-bold text-text tracking-[0.5px] leading-none tabular-nums">
          {h}:{m}
          <span className="text-xs text-text-muted font-medium">:{s}</span>
        </span>
        <span className="text-micro font-mono text-text-muted font-semibold leading-none">
          {ampm}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5 leading-none mt-0.5">
        <span className="text-xs font-mono text-text-muted font-semibold tracking-[0.5px]">
          {city}
        </span>
        <span className="text-micro text-text-muted">&middot;</span>
        <span className="text-xs font-mono text-text font-semibold tracking-[0.6px]">
          {label}
        </span>
      </div>
    </div>
  );
}

const PAGE_TITLES: Record<string, string> = {
  '/': 'THE COCKPIT',
  '/solver': 'THE SOLVER',
  '/builder': 'THE BUILDER',
  '/pipeline': 'PIPELINE',
  '/settings': 'SETTINGS',
};

interface HeaderProps {
  pathname: string;
}

export function Header({ pathname }: HeaderProps) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const title = PAGE_TITLES[pathname] || 'ARCANUM';

  return (
    <div className="h-12 bg-header-bg border-b border-panel-border flex items-center justify-between px-6 shrink-0">
      <h1 className="text-base font-semibold text-text-bright font-mono tracking-[3px]">
        {title}
      </h1>
      <div className="flex items-center gap-3.5 shrink-0 overflow-hidden">
        {/* Date */}
        <span className="text-xs text-text font-mono font-medium tracking-[0.4px]">
          {now.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
        <div className="w-px h-6 bg-panel-border" />
        {/* Globe SVG */}
        <svg
          width="24"
          height="24"
          viewBox="0 0 40 40"
          style={{ opacity: 0.45 }}
          className="shrink-0"
        >
          <defs>
            <clipPath id="hgc">
              <circle cx="20" cy="20" r="15" />
            </clipPath>
          </defs>
          <circle
            cx="20"
            cy="20"
            r="15"
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="0.9"
          />
          <g clipPath="url(#hgc)">
            <ellipse
              cx="20"
              cy="20"
              rx="15"
              ry="4.2"
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="0.5"
            />
            <ellipse
              cx="20"
              cy="12.5"
              rx="13"
              ry="2.8"
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="0.35"
            />
            <ellipse
              cx="20"
              cy="27.5"
              rx="13"
              ry="2.8"
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="0.35"
            />
            <g
              className="animate-globe-spin"
              style={{ transformOrigin: '20px 20px' }}
            >
              <ellipse
                cx="20"
                cy="20"
                rx="4"
                ry="15"
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth="0.45"
              />
              <ellipse
                cx="20"
                cy="20"
                rx="9"
                ry="15"
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth="0.4"
              />
              <ellipse
                cx="20"
                cy="20"
                rx="13.5"
                ry="15"
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth="0.35"
              />
            </g>
            <line
              x1="20"
              y1="5"
              x2="20"
              y2="35"
              stroke="var(--color-accent)"
              strokeWidth="0.5"
              opacity="0.6"
            />
          </g>
        </svg>
        <div
          className="w-px h-6"
          style={{
            background:
              'linear-gradient(to bottom, transparent, var(--color-panel-border), var(--color-panel-border), transparent)',
          }}
        />
        {/* Timezone clocks — responsive: hide extras on smaller viewports */}
        <div className="hidden xl:contents">
          <Clock
            tz={TZ_PRESETS[0].tz}
            city={TZ_PRESETS[0].city}
            label={TZ_PRESETS[0].label}
          />
          <div
            className="w-px h-7"
            style={{
              background:
                'linear-gradient(to bottom, transparent, #00c85322, #00c85322, transparent)',
            }}
          />
          <Clock
            tz={TZ_PRESETS[1].tz}
            city={TZ_PRESETS[1].city}
            label={TZ_PRESETS[1].label}
          />
          <div
            className="w-px h-7"
            style={{
              background:
                'linear-gradient(to bottom, transparent, #00c85315, #00c85315, transparent)',
            }}
          />
        </div>
        <Clock
          tz={TZ_PRESETS[2].tz}
          city={TZ_PRESETS[2].city}
          label={TZ_PRESETS[2].label}
        />
        <div className="hidden 2xl:contents">
          <div
            className="w-px h-7"
            style={{
              background:
                'linear-gradient(to bottom, transparent, #00c85315, #00c85315, transparent)',
            }}
          />
          <Clock
            tz={TZ_PRESETS[3].tz}
            city={TZ_PRESETS[3].city}
            label={TZ_PRESETS[3].label}
          />
        </div>
        <div className="w-px h-6 bg-panel-border" />
        {/* Header branding — hide when viewport is too narrow */}
        <div className="hidden 2xl:flex items-center gap-2 shrink-0">
          <svg width="14" height="14" viewBox="0 0 14 14" className="shrink-0">
            <rect
              x="0"
              y="0"
              width="4"
              height="14"
              rx="2"
              fill="var(--color-accent)"
            />
            <rect
              x="7"
              y="0"
              width="4"
              height="14"
              rx="2"
              fill="var(--color-accent)"
            />
          </svg>
          <div>
            <div className="text-sm font-bold text-text-bright font-body tracking-[1.5px] leading-none">
              ARCANUM
            </div>
            <div className="text-micro text-text-dim font-mono tracking-[1px] leading-none mt-px">
              AI UNDERWRITING
            </div>
          </div>
        </div>
        <div className="w-px h-6 bg-panel-border" />
        {/* HeaderShareBell — external-sharing Phase 3 Session 3.1 (2026-05-09).
           Self-gates on isApproved; renders null otherwise.
           Always rendered as a slot so the layout is stable across viewports. */}
        <HeaderShareBell />
      </div>
    </div>
  );
}
